import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, RefreshCw, LogOut, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const PendingApproval = () => {
  const { user, isApproved, logout, refreshApprovalStatus } = useAuth();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(new Date());

  // Auto-check approval status every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const approved = await refreshApprovalStatus();
      setLastChecked(new Date());
      if (approved) {
        navigate('/');
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshApprovalStatus, navigate]);

  // Redirect if already approved
  useEffect(() => {
    if (isApproved) {
      navigate('/');
    }
  }, [isApproved, navigate]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const approved = await refreshApprovalStatus();
    setLastChecked(new Date());
    setIsRefreshing(false);
    if (approved) {
      navigate('/');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-surface-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/emblem.svg"
            alt="BSR Robotics Logo"
            className="w-16 h-16 rounded-lg"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-slate-100">
          BSR Robotics
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-850 py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border border-surface-600/30">
          <div className="text-center">
            {/* Pending Icon */}
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>

            <h3 className="text-xl font-semibold text-slate-100 mb-2">
              Account Pending Approval
            </h3>

            <p className="text-sm text-slate-400 mb-6">
              Your account has been created successfully. An administrator needs to approve your account before you can access the dashboard.
            </p>

            {/* User Info */}
            <div className="bg-surface-900 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-slate-300">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{user.email}</span>
              </div>
            </div>

            {/* Status Check */}
            <div className="mb-6">
              <p className="text-xs text-slate-500 mb-3">
                Last checked: {formatTime(lastChecked)}
              </p>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Checking...' : 'Check Status'}
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h4 className="text-sm font-medium text-blue-800 mb-2">What happens next?</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>1. An administrator will review your account</li>
                <li>2. Once approved, refresh this page to access the dashboard</li>
                <li>3. This page auto-checks every 30 seconds</li>
              </ul>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Sign out</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;
